import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { FaPlus, FaTrashAlt, FaMoneyBillWave } from 'react-icons/fa';
import menuPortalProps from 'helpers/reactSelectPortal';
import { createExpense, updateExpense } from 'redux/booking/travel/setting/expenseSlice';
import { EMPLOYEE_STAFF_POLICY, TRAVEL_SETTING_EXPENSE_TYPE, TRAVEL_TYPE_OF_TRAVEL, TRAVEL_SETTING_EXPENSE_UNIT } from 'app_config';
import { getPositionGroups } from 'redux/organization/employeeSlice';
import { getCountries } from 'redux/settingsSlice';
import { Drawer, Button, FormRow } from 'components/common';

import { INPUT_CLS as inputClass } from 'helpers/formStyles';
const RadioRow = ({ options, value, onChange, toLower = false }) => (
  <div className="tw-flex tw-flex-wrap tw-gap-4 tw-mt-1">
    {options.map((option) => {
      const optionVal = toLower ? option.toLocaleLowerCase() : option;
      return (
        <label key={option} className="tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer">
          <input type="radio" checked={value === optionVal} onChange={() => onChange(optionVal)} />
          <span className="tw-text-sm tw-text-slate-600">{option}</span>
        </label>
      );
    })}
  </div>
);
RadioRow.propTypes = { options: PropTypes.array, value: PropTypes.string, onChange: PropTypes.func, toLower: PropTypes.bool };

const emptyRate = () => ({ key: Math.random().toString(36).slice(2), id: null, position_group_id: null, amount: '', remark: '' });

const ExpenseForm = ({ showModal, setshowModal, title, record }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue, reset } = useForm();
  const { positionGroups } = useSelector((state) => state.organization_employee);
  const { countryData } = useSelector((state) => state.settings);
  const dispatch = useDispatch();

  // Per-position-group rate rows edited inline; removed existing rows are
  // tracked so we can send `_destroy` to the nested-attributes endpoint.
  const [rates, setRates] = useState([emptyRate()]);
  const [removedRateIds, setRemovedRateIds] = useState([]);

  useEffect(() => {
    if (positionGroups?.length === 0) dispatch(getPositionGroups());
    if (countryData?.length === 0) dispatch(getCountries());
    // mount-only lookup fetch; depending on the list lengths would refetch in a loop whenever a list comes back empty
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (title === 'New Expense policy') reset(); }, [title, reset]);

  useEffect(() => {
    setValue('area', TRAVEL_TYPE_OF_TRAVEL[0]);
    setValue('expense_type', TRAVEL_SETTING_EXPENSE_TYPE[0]);
    setValue('staff_policy', EMPLOYEE_STAFF_POLICY[0].toLocaleLowerCase());
    setValue('unit', TRAVEL_SETTING_EXPENSE_UNIT[0]);
    setRemovedRateIds([]);
    if (record) {
      ['name', 'area', 'expense_type', 'staff_policy', 'unit', 'remark'].forEach((k) => setValue(k, record[k]));
      if (record.staff_policy) setValue('staff_policy', record.staff_policy.toLocaleLowerCase());
      if (record.countries) setValue('countries', record.countries.map((e) => e));
      const seeded = (record.rates || []).map((r) => ({ key: `r${r.id}`, id: r.id, position_group_id: r.position_group_id, amount: r.amount ?? '', remark: r.remark ?? '' }));
      setRates(seeded.length ? seeded : [emptyRate()]);
    } else {
      setRates([emptyRate()]);
    }
  }, [setValue, record]);

  const closeModal = () => setshowModal(false);

  const addRate = () => setRates((prev) => [...prev, emptyRate()]);
  const removeRate = (key) => setRates((prev) => {
    const target = prev.find((r) => r.key === key);
    if (target?.id) setRemovedRateIds((ids) => [...ids, target.id]);
    const next = prev.filter((r) => r.key !== key);
    return next.length ? next : [emptyRate()];
  });
  const updateRate = (key, patch) => setRates((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const onSubmit = (data) => {
    const validRates = rates.filter((r) => r.position_group_id && r.amount !== '' && r.amount !== null);
    if (validRates.length === 0) {
      toast.error('Add at least one position group with an amount.');
      return;
    }
    const rates_attributes = [
      ...validRates.map((r) => ({ id: r.id || undefined, position_group_id: r.position_group_id, amount: r.amount, remark: r.remark })),
      ...removedRateIds.map((id) => ({ id, _destroy: true })),
    ];
    const payload = { ...data, staff_policy: data?.staff_policy?.toLocaleLowerCase(), rates_attributes };
    try {
      if (record) dispatch(updateExpense({ expenseId: record.id, data: payload }));
      else dispatch(createExpense(payload));
      closeModal();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleChangeCountries = (opts) => setValue('countries', opts.map((o) => o.value), { shouldValidate: true });

  const unitOptions = useMemo(() => TRAVEL_SETTING_EXPENSE_UNIT.map((o) => ({ value: o, label: o })), []);
  const positionGroupOptions = (positionGroups || []).map((item) => ({ value: item.id, label: item.name }));
  const countryOptions = useMemo(() => (countryData || []).map((o) => ({ value: o.country, label: o.name })), [countryData]);
  const defaultCountryOption = useMemo(() => {
    if (record && record.countries && countryData) {
      return record.countries.map((code) => {
        const c = countryData.find((d) => d.country === code);
        return c ? { value: c.country, label: c.name } : null;
      }).filter(Boolean);
    }
    return null;
  }, [record, countryData]);

  return (
    <Drawer
      show={showModal}
      onHide={closeModal}
      title={title}
      footer={(
        <>
          <Button size="sm" variant="secondary" onClick={closeModal}>Cancel</Button>
          <Button size="sm" type="submit" form="expense-form" variant="primary">Save</Button>
        </>
      )}
    >
      <form id="expense-form" onSubmit={handleSubmit(onSubmit)}>
        <FormRow label="Application scope" error={errors.name?.message}>
          <textarea placeholder="Application scope" className={inputClass} {...register('name', { required: 'Application scope must be required.' })} />
        </FormRow>

        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-4">
          <FormRow label="Area">
            <Controller control={control} name="area" rules={{ required: 'Area must be required.' }} defaultValue={record?.area || TRAVEL_TYPE_OF_TRAVEL[0]}
              render={({ field }) => <RadioRow options={TRAVEL_TYPE_OF_TRAVEL} value={field.value} onChange={field.onChange} />} />
          </FormRow>

          <FormRow label="Expense type" error={errors.expense_type?.message}>
            <Controller control={control} name="expense_type" rules={{ required: 'Expense type must be required.' }} defaultValue={record?.expense_type || TRAVEL_SETTING_EXPENSE_TYPE[0]}
              render={({ field }) => <RadioRow options={TRAVEL_SETTING_EXPENSE_TYPE} value={field.value} onChange={field.onChange} />} />
          </FormRow>
        </div>

        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-4">
          <FormRow label="Staff policy" error={errors.staff_policy?.message}>
            <Controller control={control} name="staff_policy" rules={{ required: 'Staff policy must be required.' }} defaultValue={record?.staff_policy?.toLocaleLowerCase() || EMPLOYEE_STAFF_POLICY[0].toLocaleLowerCase()}
              render={({ field }) => <RadioRow options={EMPLOYEE_STAFF_POLICY} value={field.value} onChange={field.onChange} toLower />} />
          </FormRow>

          <FormRow label="Unit" error={errors.unit?.message}>
            <Controller control={control} name="unit" rules={{ required: 'Unit must be required.' }}
              render={({ field }) => (
                <Select {...field} closeMenuOnSelect options={unitOptions} value={unitOptions.find((o) => o.value === field.value) || null} placeholder="Select..." classNamePrefix="react-select" {...menuPortalProps}
                  onChange={(opt) => setValue('unit', opt.value)} />
              )} />
          </FormRow>
        </div>

        <FormRow label="Countries scope" error={errors.countries?.message}>
          <Controller control={control} name="countries"
            render={() => (
              <Select closeMenuOnSelect={false} options={countryOptions} defaultValue={defaultCountryOption} placeholder="Select..." classNamePrefix="react-select" {...menuPortalProps} isMulti onChange={handleChangeCountries} />
            )} />
        </FormRow>

        {/* Per-position-group rates — the part that used to be a duplicate row each */}
        <div className="tw-mb-4 tw-border tw-border-solid tw-border-slate-200 tw-rounded-lg tw-overflow-hidden">
          <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-bg-slate-50 tw-border-b tw-border-solid tw-border-slate-200">
            <span className="tw-flex tw-items-center tw-gap-2 tw-font-semibold tw-text-slate-700 tw-text-sm">
              <FaMoneyBillWave className="tw-text-blue-600" /> Position group amounts
            </span>
            <Button variant="secondary" size="sm" leftIcon={FaPlus} onClick={addRate}>Add position group</Button>
          </div>
          <div className="tw-p-4 tw-space-y-3">
            {rates.map((rate) => (
              <div key={rate.key} className="tw-p-3 tw-rounded tw-border tw-border-solid tw-border-slate-200 tw-bg-slate-50">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <div className="tw-flex-1">
                    <Select
                      closeMenuOnSelect
                      options={positionGroupOptions}
                      value={positionGroupOptions.find((o) => o.value === rate.position_group_id) || null}
                      placeholder="Position group..."
                      classNamePrefix="react-select" {...menuPortalProps}
                      onChange={(opt) => updateRate(rate.key, { position_group_id: opt.value })}
                    />
                  </div>
                  <div className="tw-w-36 tw-flex-shrink-0">
                    <input type="number" step="any" placeholder="Amount" className={inputClass} value={rate.amount}
                      onChange={(e) => updateRate(rate.key, { amount: e.target.value })} />
                  </div>
                  <Button variant="danger" size="sm" leftIcon={FaTrashAlt} onClick={() => removeRate(rate.key)} />
                </div>
                <input type="text" placeholder="Remark (e.g. 3-star hotel not exceed 80 USD/day, Actual cost…)" className={`${inputClass} tw-mt-2`} value={rate.remark}
                  onChange={(e) => updateRate(rate.key, { remark: e.target.value })} />
              </div>
            ))}
            <p className="tw-text-xs tw-text-slate-400 tw-mb-0 tw-mt-1">Amount unit follows the selected Unit above. Remark is per position group (e.g. hotel policy differs by level).</p>
          </div>
        </div>
      </form>
    </Drawer>
  );
};

ExpenseForm.propTypes = {
  showModal: PropTypes.bool,
  setshowModal: PropTypes.func,
  title: PropTypes.string,
  record: PropTypes.object,
};

export default ExpenseForm;
